/**
 * deviceCheckViewModel — la vue du test du matériel (`deriveDeviceCheck`) traduite en props des
 * pièces du design system : `<DeviceCheckPanel>`, l'étape ouverte (`<DeviceCheckStep>`), l'aperçu
 * (`<CameraPreviewFrame>`), le test du son (`<DeviceCheckSoundTest>`), et l'état de l'entrée de la
 * page (`entry`).
 *
 * Pur : les gestes restent au crochet du moteur, l'écran les branche. Les mots des boutons sont ceux
 * de l'ancien `PreCallCheckPanel` ; les gestes du conseil gardent leur glyphe quand le design system
 * le connaît, la ligne seule sinon.
 */
import { type DeviceCheckView, type DeviceGuidanceLine } from "../../../../../engine/precall/deviceCheckMachine"
import { type IconName } from "../../../../../design-system/atoms/icons/Icon"
import { type UIGuidanceLine } from "../../../../../design-system/molecules/DeviceCheckGuidance"
import { type UISoundTestState } from "../../../../../design-system/molecules/DeviceCheckSoundTest"
import { type UIDeviceCheckAction } from "../../../../../design-system/organisms/DeviceCheckStep"
import { type UIDeviceCheckLine } from "../../../ui/types/studentDeviceCheck"

export type StudentLiveEntryEntry = "before" | "during" | "ready"

export type DeviceCheckStepVM = {
    line: UIDeviceCheckLine
    resolvedText?: string
    listening: boolean
    showsMicLevel: boolean
    guidance?: { lines: UIGuidanceLine[]; permission?: { device: "microphone" | "camera"; afterIndex: number } }
    actions: UIDeviceCheckAction[]
    devices: { kind: "audioinput" | "videoinput"; options: { id: string; label: string }[]; selectedId: string } | null
    /** « Ça n'a pas marché ». */
    nextAttempt: boolean
    soundTest: { state: UISoundTestState; pointer: "click" | "tap"; blockedBy: string; answerable: boolean } | null
}

export type DeviceCheckVM = {
    phase: "intro" | "journey" | "remembered"
    memoryMiss?: string
    starting: boolean
    lines: UIDeviceCheckLine[]
    currentIndex: number | null
    rememberedPending: boolean
    /** La carte « déjà vérifié » montre l'aperçu ; l'attente de la vérification silencieuse, non. */
    rememberedPreview: boolean
    canRecheck: boolean
    step: DeviceCheckStepVM | null
    preview: { videoOff: boolean; obstructed: boolean; deviceError?: string }
    entry: StudentLiveEntryEntry
}

/** Les glyphes des échelles de dépannage (des noms FontAwesome 5) que le design system dessine. */
const GUIDANCE_ICONS: Record<string, IconName> = {
    "arrows-alt-h": "arrows-alt-h",
    cog: "cog",
    comment: "comment",
    "door-open": "door-open",
    ethernet: "ethernet",
    eye: "eye",
    font: "font",
    "hand-paper": "hand-paper",
    headphones: "headphones",
    keyboard: "keyboard",
    lightbulb: "lightbulb",
    lock: "lock",
    microphone: "microphone",
    "microphone-slash": "microphone-slash",
    "mobile-alt": "mobile-alt",
    redo: "redo",
    "sliders-h": "sliders-h",
    "sync-alt": "sync-alt",
    times: "times",
    unlock: "unlock",
    "video-slash": "video-slash",
    video: "video",
    "volume-mute": "volume-mute",
    "volume-up": "volume-up",
    wifi: "wifi",
    "window-close": "window-close",
    // Le retour au téléphone, au chevron (R53). La bascule « Autoriser » est partie avec les chemins des
    // navigateurs (`browserPermissionPaths`), qui dessinent l'appareil.
    "arrow-left": "chevron-left",
}

export const toGuidanceLine = (line: DeviceGuidanceLine): UIGuidanceLine => ({
    text: line.text,
    iconName: line.icon != null ? GUIDANCE_ICONS[line.icon] : undefined,
})

type ActionCopy = { label: string; iconName?: IconName; emphasis?: "primary" | "secondary" }

/** Les gestes de l'étape, dans l'ordre de l'ancien écran ; le son et l'essai suivant ont leurs places à eux. */
const ACTION_ORDER = [
    "mic-no-signal",
    "open-settings",
    "authorize",
    "mute-video",
    "enable-video",
    "sees-self-yes",
    "sees-self-no",
]

function actionCopy(key: string, busy: boolean, seesSelfDenied: boolean): ActionCopy | null {
    switch (key) {
        case "mic-no-signal":
            return { label: "Je ne vois rien bouger", emphasis: "secondary" }
        case "open-settings":
            return { label: "Ouvrir les réglages", iconName: "cog" }
        case "authorize":
            return { label: busy ? "Vérification…" : "Autoriser", iconName: "unlock" }
        case "mute-video":
            return { label: "Couper ma vidéo" }
        case "enable-video":
            return { label: "Activer ma vidéo", iconName: "video" }
        case "sees-self-yes":
            return { label: "Oui, je me vois bien" }
        case "sees-self-no":
            return { label: seesSelfDenied ? "Toujours pas" : "Non, je ne me vois pas", emphasis: "secondary" }
        default:
            return null
    }
}

/**
 * Le test du matériel s'ouvre dès qu'on sait quoi servir : la séance lue, ou six secondes passées
 * sans elle — l'ancien écran servait alors son test d'avant (`LegacyPreCallCheck`) plutôt que rien.
 * Sans séance, l'équipe n'est pas prévenue (`technicianAlert` attend la séance).
 */
export function entryTestShown(found: boolean, sessionTimedOut: boolean): boolean {
    return found || sessionTimedOut
}

export function deviceCheckViewModel(args: {
    view: DeviceCheckView
    isWeb: boolean
    isVideoMuted: boolean
}): DeviceCheckVM {
    const { view, isWeb, isVideoMuted } = args
    const lines: UIDeviceCheckLine[] = view.steps.map((step) => ({
        id: step.id,
        label: step.label,
        detail: step.detail,
        verdict: step.skipped ? "bad" : step.acquiredVerdict,
        skipped: step.skipped || undefined,
    }))

    const current = view.current
    let step: DeviceCheckStepVM | null = null
    if (current != null && view.currentIndex != null && view.phase === "journey") {
        const actions = ACTION_ORDER.filter((key) => current.actions.includes(key)).flatMap((key) => {
            const copy = actionCopy(key, view.busy, current.seesSelfDenied)
            return copy == null
                ? []
                : [
                      {
                          key,
                          label: copy.label,
                          iconName: copy.iconName,
                          emphasis: copy.emphasis,
                          loading: key === "authorize" && view.busy,
                          disabled: key === "authorize" && view.busy,
                      },
                  ]
        })
        const picker = current.picker
        step = {
            line: lines[view.currentIndex]!,
            resolvedText: current.resolved ? current.resolvedText : undefined,
            listening: current.listening,
            showsMicLevel: current.showsMicLevel,
            guidance:
                current.guidance.length > 0
                    ? {
                          lines: current.guidance.map(toGuidanceLine),
                          permission: current.permissionHint ?? undefined,
                      }
                    : undefined,
            actions,
            devices:
                picker != null && picker.options.length > 0
                    ? {
                          kind: picker.kind,
                          options: picker.options.map((option, index) => ({
                              id: option.deviceId,
                              label:
                                  option.label || `${picker.kind === "audioinput" ? "Micro" : "Caméra"} ${index + 1}`,
                          })),
                          // Sans choix, le premier de la liste est celui qui écoute.
                          selectedId: picker.selectedId ?? picker.options[0]!.deviceId,
                      }
                    : null,
            nextAttempt: current.actions.includes("next-attempt"),
            soundTest:
                current.sound != null
                    ? {
                          state: current.sound,
                          pointer: isWeb ? "click" : "tap",
                          blockedBy: current.soundBlockedBy,
                          answerable: current.actions.includes("heard-yes"),
                      }
                    : null,
        }
    }

    return {
        phase: view.phase === "probing" ? "remembered" : view.phase,
        memoryMiss: view.memoryMiss !== "" ? view.memoryMiss : undefined,
        starting: view.busy,
        lines,
        currentIndex: view.currentIndex,
        rememberedPending: view.phase === "probing" || view.rememberedPending,
        rememberedPreview: view.phase === "remembered",
        canRecheck: view.phase === "remembered",
        step,
        preview: {
            videoOff: isVideoMuted,
            obstructed: view.obstructed,
            deviceError: view.previewError ?? undefined,
        },
        entry: view.report.allDone ? "ready" : view.report.started ? "during" : "before",
    }
}
